import { Box, Flex, Text } from '@mantine/core';
import { renderCell } from '../renderCell';
import { ReadOnlyFundCell } from './ReadOnlyFundCell';
import {
  BORDER_COLOR,
  CELL_PADDING,
  DISABLED_TEXT_COLOR,
  FONT_SIZE,
  INDENT_SIZE,
  LEFT_SECTION_WIDTH,
  LINE_HEIGHT,
  TEXT_COLOR,
  HEADER_BG,
  useTableStyles
} from '../tableConstants';
import { SeeMoreText } from './SeeMoreText';
import { useReportContext, useReportContextOptional } from '../../../context/ReportContext';
// import { TbChevronDownLeft } from 'react-icons/tb';
import { useSectionKey } from '../../../context/SectionKeyContext';
import { idCreator } from '../../../../../Project/utils/idCreator';

export const MultiFundRow = ({
  columnSchema,
  cellSchema,
  levelIndex = 0,
  fundKeys = [],
  funds,
  getValue,
  onFundChange,
  cellRefs,
  onCellKeyDown,
  getCellKey,
  label,
  columnLevel,
  readOnly = false,
  tableName,
  fieldName,
  hasDrillDownFeature,
  symbol
}) => {
  const { isComparisonMode } = useReportContext();
  const reportContext = useReportContextOptional();
  const isStaticTemplateModalOpen = Boolean(reportContext?.isStaticTemplateModalOpen);
  const sectionKey = useSectionKey();
  const columnWidth = `${100 / Math.max(fundKeys.length, 1)}%`;
  const rowLabel = label || columnSchema?.name;
  const rowLevel = columnLevel || columnSchema?.level;
  const { classes } = useTableStyles();
  const rowId = idCreator([tableName, fieldName, rowLevel, rowLabel].filter(Boolean).join('-'));
  return (
    <Flex id={`formpf-multi-fund-row-${rowId}`} miw="min-content" className={classes.borderBottom}>
      <Flex
        id={`formpf-multi-fund-row-left-${rowId}`}
        maw={LEFT_SECTION_WIDTH}
        miw={LEFT_SECTION_WIDTH}
        className={classes.leftSticky}
        bg="#fff"
      >
        {Array.from({ length: levelIndex }).map((_, index) => (
          <Flex
            key={index}
            align="center"
            justify="center"
            maw={INDENT_SIZE}
            miw={INDENT_SIZE}
            className={classes.borderRight}
          />
        ))}

        {rowLevel && (
          <Text
            id={`formpf-multi-fund-row-level-${rowId}`}
            p={CELL_PADDING}
            miw={`${INDENT_SIZE}px`}
            ta="center"
            c={TEXT_COLOR}
            fz={FONT_SIZE}
            lh={LINE_HEIGHT}
            className={classes.borderRight}
          >
            {rowLevel}
          </Text>
        )}

        <Text
          id={`formpf-multi-fund-row-label-${rowId}`}
          p={CELL_PADDING}
          flex="auto"
          c={TEXT_COLOR}
          fz={FONT_SIZE}
          lh={LINE_HEIGHT}
          className={classes.borderRight}
        >
          <SeeMoreText text={rowLabel} />
        </Text>
      </Flex>
      <Flex flex="auto" className={classes.borderRight}>
        {fundKeys.map((fundKey, index) => {
          const row = (getValue ? getValue(fundKey) : null) || {};
          const fieldDifference = row?.difference?.[fieldName];
          const isFieldChanged = Boolean(fieldDifference?.is_changed);
          const hasChangePercent =
            fieldDifference?.change_percent !== null &&
            fieldDifference?.change_percent !== undefined;
          const changeDirection = fieldDifference?.change_direction;
          const required = funds[fundKey]?.required_sections;
          const isSectionDisabled =
            sectionKey &&
            Array.isArray(required) &&
            required.length > 0 &&
            !required.includes(sectionKey);
          return (
            <Box
              id={`formpf-multi-fund-row-cell-${rowId}-${fundKey}`}
              key={fundKey}
              miw={
                fundKeys.length > 3 &&
                window.innerWidth < fundKeys.length * 260 + LEFT_SECTION_WIDTH
                  ? '260px'
                  : columnWidth
              }
              maw={
                fundKeys.length > 3 &&
                window.innerWidth < fundKeys.length * 260 + LEFT_SECTION_WIDTH
                  ? '260px'
                  : columnWidth
              }
              sx={() => ({
                borderRight: index === fundKeys.length - 1 ? 'none' : `1px solid ${BORDER_COLOR}`,
                background: isComparisonMode
                  ? index % 2 === 0
                    ? HEADER_BG
                    : '#fff'
                  : readOnly ||
                      isSectionDisabled ||
                      row?.['static_template_fields']?.includes(fieldName)
                    ? HEADER_BG
                    : '#fff'
              })}
            >
              {isSectionDisabled ? (
                <Text
                  id={`formpf-multi-fund-row-na-${rowId}-${fundKey}`}
                  p={CELL_PADDING}
                  bg={HEADER_BG}
                  c={DISABLED_TEXT_COLOR}
                  fz={FONT_SIZE}
                  lh={LINE_HEIGHT}
                  ta="center"
                >
                  N/A
                </Text>
              ) : readOnly || isComparisonMode ? (
                <ReadOnlyFundCell
                  id={`formpf-multi-fund-row-readonly-${rowId}-${fundKey}`}
                  value={row[fieldName] || ''}
                  isFieldChanged={isFieldChanged}
                  hasChangePercent={hasChangePercent}
                  changePercent={fieldDifference?.change_percent}
                  changeDirection={changeDirection}
                  error={row?.errors?.[fieldName]}
                  showComment={readOnly}
                  fundId={funds[fundKey]?.fund_id}
                  fundName={funds[fundKey]?.fund_name}
                  tableName={tableName}
                  fieldName={fieldName}
                  fieldOrder={rowLevel}
                />
              ) : (
                renderCell({
                  columnSchema: cellSchema,
                  value: row[fieldName],
                  onCellChange: value => onFundChange?.(fundKey, value),
                  error: row?.errors?.[fieldName] || null,
                  cellRefs,
                  cellKey: getCellKey ? getCellKey(fundKey) : null,
                  onCellKeyDown,
                  fundId: funds[fundKey]?.fund_id,
                  fundName: funds[fundKey]?.fund_name,
                  tableName,
                  fieldName,
                  fieldOrder: rowLevel,
                  hasDrillDownFeature:
                    row[fieldName] !== null &&
                    row[fieldName] !== undefined &&
                    hasDrillDownFeature &&
                    row?.['report_id'] &&
                    row?.['id'],
                  reportId: row?.['report_id'],
                  tableId: row?.['id'],
                  title: rowLabel,
                  isFromStaticTemplate: row?.['static_template_fields']?.includes(fieldName),
                  isStaticTemplateModalOpen,
                  symbol
                })
              )}
            </Box>
          );
        })}
      </Flex>
    </Flex>
  );
};
